import {DataTable} from "../DataTable/DataTable";
import {FleetAssetCapabilityType} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {
  useFleetAssetCapabilityDataTableFilter
} from "../FleetAssetCapabilityDataTable/useFleetAssetCapabilityDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import { FormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: FleetAssetCapabilityType[] = []

export type BaseFleetAssetCapabilityDataTableProps = EntityDataTableProps<FleetAssetCapabilityType> & {
  renderActions: (record: FleetAssetCapabilityType, index: number) => ReactNode
}

export const BaseFleetAssetCapabilityDataTable = (props: BaseFleetAssetCapabilityDataTableProps) => {
  const {
    records: permissions = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useFleetAssetCapabilityDataTableFilter({
    records: permissions
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.code?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<FleetAssetCapabilityType>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.fleetassetcapabilitytype.table.column.name", "Name"),
        accessor: 'code',
        width: 400,
        sortable: true,
        render: record => <FormattedDatabaseCode value={record} />
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, renderActions, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
